import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Mail,
  ChevronDown,
  ChevronRight,
  Paperclip,
  Send,
  Users,
  FileEdit,
  Trash2,
} from "lucide-react";
import {
  CrewEmail,
  useCrewEmails,
  useDeleteDraft,
} from "@/hooks/useCrewEmails";
import { ProjectAvailability } from "@/hooks/useProjectPeople";
import { SendCrewEmailDialog } from "./SendCrewEmailDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CrewEmailHistoryProps {
  projectId: string;
  projectName: string;
  availability: ProjectAvailability[];
}

function SentEmailRow({
  email,
  projectId,
  projectName,
  availability,
}: {
  email: CrewEmail;
  projectId: string;
  projectName: string;
  availability: ProjectAvailability[];
}) {
  const [expanded, setExpanded] = useState(false);

  const recipientCount = email.recipients?.length ?? 0;
  const attachmentCount = email.attachments?.length ?? 0;

  // Check if there are new booked crew members who haven't received this email
  const sentPersonIds = new Set(
    email.recipients?.map((r) => r.person_id) ?? [],
  );
  const newCrewWithEmail = availability.filter(
    (a) =>
      a.person?.email &&
      a.status === "booked" &&
      !sentPersonIds.has(a.person_id),
  );
  const hasNewRecipients = newCrewWithEmail.length > 0;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="border rounded-md">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors">
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{email.subject}</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(email.created_at), "dd.MM.yyyy HH:mm")}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="secondary" className="text-xs gap-1">
                <Users className="h-3 w-3" />
                {recipientCount}
              </Badge>
              {attachmentCount > 0 && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Paperclip className="h-3 w-3" />
                  {attachmentCount}
                </Badge>
              )}
              {hasNewRecipients && (
                <Badge className="text-xs gap-1">
                  +{newCrewWithEmail.length} new
                </Badge>
              )}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 border-t space-y-3">
            {/* Recipients */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">
                Sent to:
              </p>
              <div className="flex flex-wrap gap-1">
                {email.recipients?.map((r) => (
                  <Badge key={r.id} variant="outline" className="text-xs">
                    {r.person
                      ? `${r.person.first_name} ${r.person.last_name}`
                      : r.email}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Attachments:
                </p>
                <div className="flex flex-wrap gap-1">
                  {email.attachments.map((a) => (
                    <Badge
                      key={a.id}
                      variant="outline"
                      className="text-xs gap-1"
                    >
                      <Paperclip className="h-3 w-3" />
                      {a.filename}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Resend button */}
            {hasNewRecipients && (
              <SendCrewEmailDialog
                availability={availability}
                projectName={projectName}
                projectId={projectId}
                resendEmail={email}
                trigger={
                  <Button variant="outline" size="sm" className="gap-2">
                    <Send className="h-3 w-3" />
                    Send to {newCrewWithEmail.length} New Crew Member(s)
                  </Button>
                }
              />
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function DraftEmailRow({
  email,
  projectId,
  projectName,
  availability,
}: {
  email: CrewEmail;
  projectId: string;
  projectName: string;
  availability: ProjectAvailability[];
}) {
  const deleteDraft = useDeleteDraft();

  const handleDelete = () => {
    deleteDraft.mutate({ id: email.id, project_id: projectId });
  };

  const draftRecipientCount = email.draft_person_ids?.length ?? 0;

  return (
    <div className="border border-dashed rounded-md flex items-center gap-3 px-3 py-2">
      <FileEdit className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {email.subject || (
            <span className="text-muted-foreground italic">No subject</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          Draft saved {format(new Date(email.updated_at), "dd.MM.yyyy HH:mm")}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {draftRecipientCount > 0 && (
          <Badge variant="outline" className="text-xs gap-1">
            <Users className="h-3 w-3" />
            {draftRecipientCount}
          </Badge>
        )}
        <Badge variant="secondary" className="text-xs">
          Draft
        </Badge>
        <SendCrewEmailDialog
          availability={availability}
          projectName={projectName}
          projectId={projectId}
          draftEmail={email}
          trigger={
            <Button variant="outline" size="sm" className="gap-1">
              <FileEdit className="h-3 w-3" />
              Edit
            </Button>
          }
        />
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-destructive hover:text-destructive"
          onClick={handleDelete}
          disabled={deleteDraft.isPending}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export function CrewEmailHistory({
  projectId,
  projectName,
  availability,
}: CrewEmailHistoryProps) {
  const { data: emails, isLoading } = useCrewEmails(projectId);

  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  const drafts = emails?.filter((e) => e.status === "draft") ?? [];
  const sent = emails?.filter((e) => e.status !== "draft") ?? [];
  const hasEmails = drafts.length > 0 || sent.length > 0;
  const totalEmails = drafts.length + sent.length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Mail className="h-4 w-4" />
          Email History
          {totalEmails > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
              {totalEmails}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Crew Emails</DialogTitle>
          <DialogDescription>
            Drafts and sent emails for this project. Sent emails can be resent
            to newly added booked crew members.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !hasEmails ? (
            <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              No crew emails yet. Drafts and sent emails will appear here.
            </div>
          ) : (
            <div className="space-y-4">
              {drafts.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium">Drafts</h3>
                    <Badge variant="secondary">{drafts.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {drafts.map((email) => (
                      <DraftEmailRow
                        key={email.id}
                        email={email}
                        projectId={projectId}
                        projectName={projectName}
                        availability={availability}
                      />
                    ))}
                  </div>
                </section>
              )}

              {drafts.length > 0 && sent.length > 0 && <Separator />}

              {sent.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium">Sent</h3>
                    <Badge variant="outline">{sent.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {sent.map((email) => (
                      <SentEmailRow
                        key={email.id}
                        email={email}
                        projectId={projectId}
                        projectName={projectName}
                        availability={availability}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
